import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import './Scheduler.scss';
import Loader from '../../components/BaseComponents/Loader';
import { RootState } from '../../store/store';
import { UserRoleEnum } from '../../types/apiGeneratedTypes';
import {
  ScheduledTaskDto,
  cancelSchedule,
  cancelSchedulesBulk,
  listOverdueSchedules,
  listSchedules,
  runNow,
} from '../../api/scheduler';

type TabKey = 'failed' | 'processed' | 'upcoming';
type ReplayFilter = 'all' | 'replayable' | 'not_replayable';

const isSuperAdminUser = (admin: RootState['admin']['admin']) =>
  admin.role === UserRoleEnum.SuperAdmin && admin.is_super_admin === true;

const SchedulerPage = () => {
  const navigate = useNavigate();
  const adminInfo = useSelector((state: RootState) => state.admin);

  const [tab, setTab] = useState<TabKey>('upcoming');
  const [rows, setRows] = useState<ScheduledTaskDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [replayFilter, setReplayFilter] = useState<ReplayFilter>('all');

  const selectedIds = useMemo(
    () => Object.keys(selected).filter((id) => selected[id]),
    [selected]
  );

  useEffect(() => {
    if (!isSuperAdminUser(adminInfo.admin)) {
      toast.error('Not authorized');
      navigate('/', { replace: true });
    }
  }, [adminInfo.admin, navigate]);

  const fetchData = async () => {
    setIsLoading(true);
    setSelected({});
    try {
      if (tab === 'failed') {
        const data = await listOverdueSchedules(60, 200);
        setRows(data);
      } else if (tab === 'processed') {
        const data = await listSchedules({ status_filter: 'executed', limit: 200, offset: 0 });
        setRows(data);
      } else {
        const nowIso = new Date().toISOString();
        const data = await listSchedules({
          status_filter: 'scheduled',
          scheduled_from: nowIso,
          limit: 200,
          offset: 0,
        });
        setRows(data);
      }
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || 'Failed to load scheduler data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isSuperAdminUser(adminInfo.admin)) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, adminInfo.admin]);

  const filteredRows = useMemo(() => {
    if (replayFilter === 'replayable') return rows.filter((r) => !!r.task_name);
    if (replayFilter === 'not_replayable') return rows.filter((r) => !r.task_name);
    return rows;
  }, [rows, replayFilter]);

  const scheduleById = useMemo(() => {
    const map: Record<string, ScheduledTaskDto> = {};
    rows.forEach((r) => (map[r.schedule_id] = r));
    return map;
  }, [rows]);

  const canReplay = (r: ScheduledTaskDto) => !!r.task_name;
  const canCancel = (r: ScheduledTaskDto) => r.status === 'scheduled';

  const toggleAll = (checked: boolean) => {
    const next: Record<string, boolean> = {};
    filteredRows.forEach((r) => (next[r.schedule_id] = checked));
    setSelected(next);
  };

  const toggleOne = (id: string, checked: boolean) => {
    setSelected((prev) => ({ ...prev, [id]: checked }));
  };

  const doCancelOne = async (scheduleId: string) => {
    const r = scheduleById[scheduleId];
    if (r && !canCancel(r)) {
      toast.error('This task cannot be cancelled.');
      return;
    }
    try {
      await cancelSchedule(scheduleId);
      toast.success('Cancelled');
      await fetchData();
    } catch (e: any) {
      toast.error(e?.message || 'Cancel failed');
    }
  };

  const doCancelSelected = async () => {
    if (selectedIds.length === 0) return;
    const cancellable = selectedIds.filter((id) => {
      const r = scheduleById[id];
      return r ? canCancel(r) : true;
    });
    const skipped = selectedIds.length - cancellable.length;
    if (cancellable.length === 0) {
      toast.error('No selected tasks can be cancelled.');
      return;
    }
    try {
      await cancelSchedulesBulk(cancellable);
      toast.success(
        skipped > 0
          ? `Cancelled ${cancellable.length} (skipped ${skipped})`
          : `Cancelled ${cancellable.length}`
      );
      await fetchData();
    } catch (e: any) {
      toast.error(e?.message || 'Bulk cancel failed');
    }
  };

  const doRunNowOne = async (scheduleId: string) => {
    const r = scheduleById[scheduleId];
    if (r && !canReplay(r)) {
      toast.error('No payload stored: cannot replay.');
      return;
    }
    try {
      await runNow(scheduleId);
      toast.success('Enqueued');
    } catch (e: any) {
      toast.error(e?.message || 'Replay failed');
    }
  };

  const doRunNowSelected = async () => {
    if (selectedIds.length === 0) return;
    const replayableIds = selectedIds.filter((id) => {
      const r = scheduleById[id];
      return r ? canReplay(r) : false;
    });
    const skipped = selectedIds.length - replayableIds.length;
    if (replayableIds.length === 0) {
      toast.error('No selected tasks can be replayed.');
      return;
    }
    let ok = 0;
    for (const id of replayableIds) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await runNow(id);
        ok += 1;
      } catch (e) {
        // keep going
      }
    }
    toast.success(
      skipped > 0 ? `Replayed ${ok}/${replayableIds.length} (skipped ${skipped})` : `Replayed ${ok}/${replayableIds.length}`
    );
  };

  const allChecked =
    filteredRows.length > 0 && selectedIds.length === filteredRows.length;

  if (!isSuperAdminUser(adminInfo.admin)) return null;
  if (isLoading) return <Loader />;

  return (
    <div className='scheduler-page'>
      <div className='scheduler-header sideBarPad'>
        <div>
          <div className='scheduler-title'>Scheduled Tasks</div>
          <div className='scheduler-subtitle'>
            View upcoming schedules, processed tasks, and overdue (failed/stuck) schedules.
          </div>
        </div>
        <div className='scheduler-actions'>
          <select
            className='scheduler-filter'
            value={replayFilter}
            onChange={(e) => setReplayFilter(e.target.value as ReplayFilter)}
            aria-label='Replayability filter'
          >
            <option value='all'>All</option>
            <option value='replayable'>Replayable</option>
            <option value='not_replayable'>Not replayable</option>
          </select>
          <button className='btn secondary' onClick={fetchData}>
            Refresh
          </button>
          <button
            className='btn danger'
            onClick={doCancelSelected}
            disabled={selectedIds.length === 0}
          >
            Cancel selected
          </button>
          <button
            className='btn primary'
            onClick={doRunNowSelected}
            disabled={selectedIds.length === 0}
          >
            Replay selected
          </button>
        </div>
      </div>

      <div className='scheduler-tabs'>
        <button
          className={tab === 'failed' ? 'tab active' : 'tab'}
          onClick={() => setTab('failed')}
        >
          Failed / Overdue
        </button>
        <button
          className={tab === 'processed' ? 'tab active' : 'tab'}
          onClick={() => setTab('processed')}
        >
          Processed
        </button>
        <button
          className={tab === 'upcoming' ? 'tab active' : 'tab'}
          onClick={() => setTab('upcoming')}
        >
          Upcoming
        </button>
      </div>

      <div className='scheduler-table'>
        <div className='scheduler-table-head'>
          <div className='cell checkbox'>
            <input
              type='checkbox'
              checked={allChecked}
              onChange={(e) => toggleAll(e.target.checked)}
            />
          </div>
          <div className='cell scheduleId'>Schedule ID</div>
          <div className='cell when'>Scheduled at</div>
          <div className='cell type'>Task type</div>
          <div className='cell entity'>Entity</div>
          <div className='cell status'>Status</div>
          <div className='cell buttons'>Actions</div>
        </div>

        {filteredRows.length === 0 ? (
          <div className='scheduler-empty'>No tasks found.</div>
        ) : (
          filteredRows.map((r) => (
            <div className='scheduler-table-row' key={r.schedule_id}>
              <div className='cell checkbox'>
                <input
                  type='checkbox'
                  checked={!!selected[r.schedule_id]}
                  onChange={(e) => toggleOne(r.schedule_id, e.target.checked)}
                />
              </div>
              <div className='cell scheduleId mono'>{r.schedule_id}</div>
              <div className='cell when mono'>
                {new Date(r.scheduled_at).toLocaleString()}
              </div>
              <div className='cell type'>{r.task_type}</div>
              <div className='cell entity mono'>
                {r.entity_type}:{r.entity_id}
              </div>
              <div className='cell status'>{r.status}</div>
              <div className='cell buttons'>
                <button
                  className='btn small danger'
                  onClick={() => doCancelOne(r.schedule_id)}
                  disabled={!canCancel(r)}
                  title={!canCancel(r) ? 'Only scheduled tasks can be cancelled' : undefined}
                >
                  Cancel
                </button>
                <button
                  className='btn small primary'
                  onClick={() => doRunNowOne(r.schedule_id)}
                  disabled={!canReplay(r)}
                  title={!canReplay(r) ? 'No payload stored for replay' : undefined}
                >
                  Replay
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SchedulerPage;

